import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, Copy, AlertTriangle, CheckCircle2, Mail, KeyRound, Server, Shield, ExternalLink } from "lucide-react";

/**
 * One-shot modal that generates SMTP credentials for an alias and shows them
 * exactly once, along with copy-paste instructions for Gmail / Outlook / iCloud.
 */
export default function SendAsModal({ open, onClose, alias }) {
  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !alias) return;
    setCreds(null);
    setError(null);
    setLoading(true);
    api.post(`/aliases/${alias.id}/send-as-credentials`)
      .then((r) => setCreds(r.data))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, alias]);

  const copy = async (v, label = "Value") => {
    try { await navigator.clipboard.writeText(v); toast.success(`${label} copied`); } catch { /* ignore */ }
  };

  if (!open || !alias) return null;

  const GmailDeepLink = "https://mail.google.com/mail/u/0/#settings/accounts";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 overflow-y-auto" data-testid="sendas-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]">
          <div>
            <h2 className="text-xl font-bold text-[#0F172A]">Send as <span className="mono text-[#F26953]">{alias.alias}@{alias.domain}</span></h2>
            <p className="text-xs text-[#475569] mt-1">Paste these credentials into Gmail / Outlook / iCloud to send from your custom email.</p>
          </div>
          <button onClick={onClose} data-testid="sendas-close"
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5"/>
          </button>
        </div>

        {loading && (
          <div className="p-10 text-center text-sm text-[#475569]">Generating SMTP credentials…</div>
        )}

        {error && !loading && (
          <div className="p-8 text-center">
            <div className="text-sm text-red-600 font-medium">{error}</div>
          </div>
        )}

        {creds && (
          <div className="p-6 space-y-5" data-testid="sendas-content">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5"/>
              <div className="text-xs text-amber-900">
                <strong>Copy this password now — it's shown only once.</strong> If you lose it, you can reset from this screen, but your existing Gmail/Outlook config will stop working until you update it.
              </div>
            </div>

            <CredRow icon={Mail} label="Your email / From" value={creds.from_email} onCopy={copy} testId="sendas-from" />
            <CredRow icon={Server} label="SMTP server" value={creds.smtp_host} onCopy={copy} testId="sendas-host" />
            <CredRow icon={Server} label="Port (recommended)" value={`${creds.smtp_port_tls} (TLS / STARTTLS)`} onCopy={copy} copyValue={String(creds.smtp_port_tls)} testId="sendas-port" />
            <CredRow icon={KeyRound} label="Username" value={creds.smtp_username} onCopy={copy} testId="sendas-user" />
            <CredRow icon={Shield} label="Password" value={creds.smtp_password} onCopy={copy} testId="sendas-pass" isPassword />

            <div className="pt-4 border-t border-[#E2E8F0] space-y-3">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Set it up in Gmail (60 seconds)</div>
              <ol className="text-sm text-[#475569] space-y-2 list-decimal pl-5 leading-relaxed">
                <li>Open <a href={GmailDeepLink} target="_blank" rel="noreferrer" className="text-[#F26953] font-semibold hover:underline inline-flex items-center gap-1">Gmail Settings → Accounts <ExternalLink className="h-3 w-3"/></a>.</li>
                <li>Click <strong>Add another email address</strong> next to "Send mail as".</li>
                <li>Name = whatever you want shown. Email = <span className="mono">{creds.from_email}</span>. Uncheck "Treat as an alias".</li>
                <li>SMTP Server = <span className="mono">{creds.smtp_host}</span>, Port = <span className="mono">{creds.smtp_port_tls}</span>, Username = <span className="mono">{creds.smtp_username}</span>, Password = the one above.</li>
                <li>Gmail sends a confirmation code to <span className="mono">{creds.from_email}</span> — it'll forward to your regular inbox. Paste the code to finish.</li>
              </ol>
              <div className="text-[11px] text-slate-400 pt-2">
                Outlook: File → Account Settings → Add Account → use "Manual setup" with the same values.<br/>
                iCloud: Settings → Mail → Add Account → Other → same values.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CredRow({ icon: Icon, label, value, onCopy, copyValue, testId, isPassword = false }) {
  const [revealed, setRevealed] = useState(!isPassword);
  const display = isPassword && !revealed ? "•".repeat(Math.max(12, (value || "").length)) : value;
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
        <Icon className="h-3.5 w-3.5"/> {label}
      </div>
      <div className="flex items-stretch gap-2">
        <code className="flex-1 text-sm bg-slate-50 border border-[#E2E8F0] rounded-lg px-3 py-2.5 overflow-x-auto whitespace-nowrap">{display}</code>
        {isPassword && (
          <button onClick={() => setRevealed(v => !v)} data-testid={`${testId}-reveal`}
            className="px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white hover:bg-slate-50 text-xs font-semibold text-[#475569]">
            {revealed ? "Hide" : "Show"}
          </button>
        )}
        <button onClick={() => onCopy(copyValue || value, label)} data-testid={`${testId}-copy`}
          className="px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white hover:bg-slate-50 text-slate-500">
          <Copy className="h-4 w-4"/>
        </button>
      </div>
    </div>
  );
}
